import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Dotdotdot from 'react-dotdotdot';

import { BodyCopy } from '@xingternal/typography';
import { StarRating } from '@xingternal/star-rating';
import {
  trackClickKununuReview,
  trackAllReviewsClick,
  trackPageChange,
  getTrackingUrl,
} from '@xingternal/pages-tracking';
import { Card as CardWrapper } from '@xingternal/card';

import {
  FormattedTimestamp,
  ModuleHeader,
  Carousel,
} from '@xingternal/pages-common';

import { NUMBER_OF_REVIEWS_REQUESTED } from '../../config';

import styles from './Reviews.css';
import { KununuData, KununuReviews, Review } from '../../types';

type CardProps = {
  score: number;
  quote: string;
  date: string;
  link: string;
  jobStatus: string;
  position: number;
  isEditor?: boolean;
};

type ReviewsProps = {
  kununuReviews: KununuReviews;
  isEditor?: boolean;
} & Pick<KununuData, 'kununuCommentsUrl'>;

const getJobStatusCopyKey = (jobStatus: string) => {
  switch (jobStatus) {
    case 'CURRENT':
      return 'EP_KUNUNU_REVIEWER_CURRENT_JOB';
    case 'INTERNSHIP':
      return 'EP_KUNUNU_REVIEWER_INTERNSHIP_JOB';
    case 'PREVIOUS':
      return 'EP_KUNUNU_REVIEWER_FORMER_JOB';
    // not specified
    default:
      return 'EP_KUNUNU_REVIEWER_NS_JOB';
  }
};

const reviewClick = (href: string, position: number, isEditor: boolean) => {
  const pageDevider =
    window.innerWidth < 740 ? 1 : window.innerWidth < 1024 ? 2 : 3;
  const page = Math.ceil((position + 1) / pageDevider);
  trackClickKununuReview({ isEditor, page });
  window.open(href, '_blank');
};

const onAllReviewsClick = (isEditor: boolean) => {
  trackAllReviewsClick({ isEditor });
};

const Card = ({
  quote,
  score,
  date,
  link,
  jobStatus,
  position,
  isEditor = false,
}: CardProps) => {
  const currentJobMessage = getJobStatusCopyKey(jobStatus);

  return (
    <CardWrapper
      onClick={() => reviewClick(link, position, isEditor)}
      className={styles.card}
    >
      <div className={styles.cardReviewHeader}>
        <div className={styles.scoreContainer}>
          <StarRating
            rating={parseFloat(score.toFixed(1))}
            className={styles.starRating}
          />
        </div>
        <BodyCopy fontWeight='bold' lineHeight='dense' size={'small'} className={styles.date}>
          {date && (
            <FormattedTimestamp
              date={new Date(date)}
              onlyMonthAndYear
              isShortVersion
            />
          )}
        </BodyCopy>
      </div>
      <BodyCopy fontWeight='bold' lineHeight='dense' size={'small'} className={styles.quote}>
        <Dotdotdot clamp={3}>{quote}</Dotdotdot>
      </BodyCopy>
      <div className={styles.reviewsFooter}>
        <BodyCopy fontWeight='bold' lineHeight='dense' size={'small'} className={styles.employeeStatus}>
          <FormattedMessage
            id={currentJobMessage}
            defaultMessage={currentJobMessage}
          />
        </BodyCopy>
      </div>
    </CardWrapper>
  );
};

const getCarouselItems = (
  reviews: Array<{ node: Review }>,
  isEditor: boolean | undefined
) =>
  reviews.map((review, index) => (
    <Card
      isEditor={isEditor}
      key={index}
      position={index}
      score={review.node.ratingAvg}
      quote={review.node.title}
      jobStatus={review.node.jobStatus}
      date={review.node.created}
      link={getTrackingUrl({
        to: review.node.url,
        trackingKey: 'viewSingleReviews',
      })}
    />
  ));

const Reviews = ({
  kununuReviews,
  kununuCommentsUrl,
  isEditor = false,
}: ReviewsProps) => {
  const firstRender = useRef(true);
  const reviews = kununuReviews?.edges || [];
  const totalReviews = kununuReviews?.total;
  const showMore =
    totalReviews > NUMBER_OF_REVIEWS_REQUESTED
      ? {
          href: getTrackingUrl({
            to: kununuCommentsUrl,
            trackingKey: 'viewAllReviews',
          }),
          target: '_blank',
          copyKey: 'EP_KUNUNU_ALL_REVIEWS',
          number: totalReviews,
          onClick: () => onAllReviewsClick(isEditor),
          externalLink: true,
        }
        : null;

  const handlePageChange = (pageChange: unknown) => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      trackPageChange(pageChange);
    }
  };

  return (
    <div className={styles.wrapper}>
      <ModuleHeader
        headlineCopyKey="EP_KUNUNU_REVIEW_TITLE"
        showMore={showMore}
        small
      />
      <Carousel
        skin={{ carousel: styles.carousel, carouselMain: styles.carouselMain }}
        onPageChange={handlePageChange}
      >
        {getCarouselItems(reviews, isEditor)}
      </Carousel>
    </div>
  );
};

export default Reviews;
